import { logInfo } from '../utils/logging';
import { REGISTERED_APPS } from '../types/Apps';
import { IntelligemsUserData } from '../types/CustomWindow';
import { INTELLIGEMS_ENABLED_OVERRIDE_KEY } from '../constants/app';
import customLocalStorage from './storage';

export const hasIntelligems = (data: unknown): data is object => {
  return typeof data === 'object' && data !== null;
};

export const hasIntelligemsUser = (
  data: unknown,
): data is IntelligemsUserData => {
  if (!hasIntelligems(data)) {
    return false;
  }

  const maybeUser = (data as any).user;
  return (
    typeof maybeUser === 'object' &&
    maybeUser !== null &&
    typeof maybeUser?.igId === 'string'
  );
};

export const getIntelligemsUserId = (
  registeredApps: REGISTERED_APPS,
): string | null => {
  if (hasIntelligemsUser(window.igData)) {
    const userId = window.igData!.user!.igId;
    logInfo(`[Intelligems] user ID found: ${userId}`, registeredApps);
    return userId;
  }
  return null;
};

/**
 * Check if intelligems is actually available and functional
 */
export const isIntelligemsAvailable = (): boolean => {
  return !!window.igData;
};

/**
 * Log Intelligems status for a specific placement
 * @param placementName - Name of the placement (e.g., "Add to Cart App")
 * @param isEnabled - Whether Intelligems is enabled in the app settings
 * @param registeredApp - Optional registered app identifier for logging context
 */
export const logIntelligemsStatus = (
  isEnabled: boolean,
  registeredApp?: REGISTERED_APPS,
) => {
  const isInstalled = isIntelligemsAvailable();
  const message = `[Intelligems] ${
    isInstalled ? 'INSTALLED' : 'NOT INSTALLED'
  } on store, ${isEnabled ? 'ENABLED' : 'DISABLED'} in DB`;

  logInfo(message, registeredApp);
};

/**
 * Get the Intelligems enabled status with localStorage override support
 * @param originalEnabled - The original enabled status from the app config
 * @returns The final enabled status (original or overridden)
 */
export const getIntelligemsEnabledStatus = (
  originalEnabled: boolean,
): boolean => {
  // Check localStorage override for intelligems enabled
  const getIntelligemsEnabledOverride = (): boolean | null => {
    const override = customLocalStorage.getItem(
      INTELLIGEMS_ENABLED_OVERRIDE_KEY,
    );

    if (override === 'true') return true;
    if (override === 'false') return false;
    return null; // No override set
  };

  const intelligemsOverride = getIntelligemsEnabledOverride();
  const finalStatus =
    intelligemsOverride !== null ? intelligemsOverride : originalEnabled;

  if (intelligemsOverride !== null) {
    logInfo(
      `[Intelligems] Override applied: ${originalEnabled} → ${finalStatus}`,
    );
  }

  return finalStatus;
};

/**
 * Parse raw price value from intelligems into a number or undefined
 *
 * This function handles the initial parsing of price data before rounding.
 * It converts various input types to a number that can then be processed
 * by convertIntelligemsPriceToMinorUnits() for proper rounding.
 *
 * Handles various input types:
 * - null, undefined, empty string → returns undefined
 * - number → returns as-is
 * - string (e.g., "29.95") → converts to number using Number()
 * - other types → attempts conversion, returns undefined if NaN
 *
 * @param rawPrice - The raw price value from intelligems (could be string, number, null, etc.)
 * @param context - Context string for logging purposes
 * @returns The parsed number or undefined if parsing fails
 */
export function parseIntelligemsPrice(
  rawPrice: unknown,
  context: string,
): number | undefined {
  if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
    return undefined;
  }

  const price = Number(rawPrice);
  if (isNaN(price)) {
    logInfo(
      `${context}: failed to parse price: ${rawPrice} (${typeof rawPrice})`,
    );
    return undefined;
  }

  return price;
}

/**
 * Convert Intelligems price from major units to minor units with proper rounding
 *
 * Intelligems returns currency strings like "29.95" (major units)
 * Candyrack expects prices in minor units (e.g., 2995 for $29.95)
 *
 * Conversion process:
 * 1. Multiply by 100 to convert major to minor units (29.95 * 100 = 2995.0)
 * 2. Use Math.round() to handle floating point precision issues
 *    - JavaScript floating point arithmetic can cause precision errors
 *    - Example: 29.95 * 100 might result in 2994.9999999999995 instead of 2995
 *    - Math.round() ensures we get the correct integer value
 *
 * Examples:
 * - 29.95 → 2995 (29.95 * 100 = 2995.0 → Math.round(2995.0) = 2995)
 * - 19.99 → 1999 (19.99 * 100 = 1999.0 → Math.round(1999.0) = 1999)
 * - 0.01 → 1 (0.01 * 100 = 1.0 → Math.round(1.0) = 1)
 *
 * @param price - The price in major units (e.g., 29.95)
 * @returns The price in minor units (e.g., 2995) or undefined if price is falsy
 */
export function convertIntelligemsPriceToMinorUnits(
  price: number | undefined,
): number | undefined {
  return price !== undefined && price !== null
    ? Math.round(price * 100)
    : undefined;
}

/**
 * Generic function to get intelligems price by variant ID
 *
 * @param isIntelligemsEnabled - Whether intelligems is enabled
 * @param variantId - The variant ID (can be number or string)
 * @param priceType - The type of price to retrieve ('price' or 'comparePrice')
 * @returns The price in minor units or undefined
 */
function getIntelligemsPriceByVariantId(
  isIntelligemsEnabled: boolean,
  variantId?: number | string,
  priceType: 'price' | 'comparePrice' = 'price',
): number | undefined {
  const functionName = `getIntelligems${
    priceType === 'price' ? 'VariantPrice' : 'ComparePriceByVariantId'
  }`;
  const methodName =
    priceType === 'price'
      ? 'getPriceByVariantId'
      : 'getComparePriceByVariantId';

  if (!isIntelligemsEnabled || !variantId) {
    return undefined;
  }

  // Verify intelligems is actually available at the point of use
  if (!isIntelligemsAvailable()) {
    return undefined;
  }

  const rawPrice = window.igData?.price?.[methodName]?.(variantId);

  const price = parseIntelligemsPrice(
    rawPrice,
    `${functionName}: variantId ${variantId}`,
  );
  const finalPrice = convertIntelligemsPriceToMinorUnits(price);

  if (finalPrice !== undefined) {
    logInfo(`${functionName}: ${variantId} → ${finalPrice}`);
  }

  // ensure we either return a valid number or undefined
  return Number.isFinite(finalPrice) ? finalPrice : undefined;
}

export function getIntelligemsVariantPrice(
  isIntelligemsEnabled: boolean,
  variantId?: number | string,
): number | undefined {
  return getIntelligemsPriceByVariantId(
    isIntelligemsEnabled,
    variantId,
    'price',
  );
}

export function getIntelligemsComparePriceByVariantId(
  isIntelligemsEnabled: boolean,
  variantId?: number | string,
): number | null | undefined {
  const functionName = 'getIntelligemsComparePriceByVariantId';
  const methodName = 'getComparePriceByVariantId';

  if (!isIntelligemsEnabled || !variantId) {
    return undefined;
  }

  // Verify intelligems is actually available at the point of use
  if (!isIntelligemsAvailable()) {
    return undefined;
  }

  const rawPrice = window.igData?.price?.[methodName]?.(variantId);

  // Special handling for compare price: distinguish between null (no compare price) and undefined (method not available)
  if (rawPrice === null || rawPrice === '') {
    return null;
  }

  if (rawPrice === undefined) {
    return undefined;
  }

  const price = parseIntelligemsPrice(
    rawPrice,
    `${functionName}: variantId ${variantId}`,
  );
  const finalPrice = convertIntelligemsPriceToMinorUnits(price);

  if (finalPrice !== undefined) {
    logInfo(`${functionName}: ${variantId} → ${finalPrice}`);
  }

  // ensure we either return a valid number, null, or undefined
  return Number.isFinite(finalPrice) ? finalPrice : undefined;
}
